import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Text from '../text';
import ConfirmationButtons from '../button/ConfirmationButtons';
import useCheckUserIsOnline from 'hooks/useCheckUserOnline';
import useRenameSite, { SITE_NAME_MAX_LENGTH } from 'hooks/useRenameSite';
import { IReduxState } from 'utils/models/redux-states';
import { isDefaultSiteName } from 'utils/constants/starterSite';
import DefaultNameTag from './DefaultNameTag';

interface SiteNameEditorProps {
	siteId: number;
	siteName?: string;
	canRename?: boolean;
	// Bumped by other controls on the card (e.g. "Rename site" in the starter panel) to open the editor.
	editRequest?: number;
}

const TITLE_STYLE =
	'!font-inter !text-xl !font-semibold text-center !leading-6 font-bold !text-[#2C2C2C] !tracking-[0.15px]';

const SiteNameEditor: React.FC<SiteNameEditorProps> = ({ siteId, siteName, canRename, editRequest }) => {
	const { isOnline } = useCheckUserIsOnline();
	const viewedUserId = useSelector((state: IReduxState) => state.user?.viewedUserInfo ?? 0);
	const { renameSite, isSaving } = useRenameSite(siteId);
	const [isEditing, setIsEditing] = useState(false);
	const [draftName, setDraftName] = useState(siteName ?? '');

	//no offline support for this
	const showRename = !!canRename && viewedUserId === 0 && isOnline;
	const trimmedDraft = draftName.trim();
	const canSave =
		!isSaving &&
		trimmedDraft !== '' &&
		trimmedDraft.length <= SITE_NAME_MAX_LENGTH &&
		trimmedDraft !== (siteName ?? '').trim();

	const startEditing = () => {
		setDraftName(siteName ?? '');
		setIsEditing(true);
	};

	useEffect(() => {
		if (editRequest) startEditing();
		// Only a new request opens the editor; siteName changes must not.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editRequest]);

	const cancelEditing = () => {
		if (isSaving) return;
		setIsEditing(false);
	};

	const save = async () => {
		if (!canSave) {
			if (trimmedDraft === (siteName ?? '').trim()) setIsEditing(false);
			return;
		}
		const saved = await renameSite(trimmedDraft);
		if (saved) setIsEditing(false);
	};

	if (isEditing && showRename) {
		return (
			// Shrinks below 320px on narrow cards so the toolbar buttons stay visible.
			<div className="flex basis-[360px] shrink items-center gap-1 min-w-0">
				<TextField
					size="small"
					autoFocus
					value={draftName}
					onChange={(e) => setDraftName(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') void save();
						if (e.key === 'Escape') cancelEditing();
					}}
					disabled={isSaving}
					error={trimmedDraft === ''}
					helperText={trimmedDraft === '' ? 'Enter a site name' : undefined}
					inputProps={{ 'aria-label': 'Site name', maxLength: SITE_NAME_MAX_LENGTH }}
					sx={{ flex: 1, minWidth: 120 }}
				/>
				<div className="flex shrink-0">
					<ConfirmationButtons
						handleConfirm={save}
						handleCancel={cancelEditing}
						disabled={isSaving}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="flex items-center gap-1">
			<Text addStyle={TITLE_STYLE}>{siteName}</Text>
			{showRename && (
				<Tooltip title="Rename site">
					<IconButton aria-label="Rename site" onClick={startEditing} size="small">
						<img src="/assets/icons/Edit.svg" alt="" className="w-[18px] h-[18px] opacity-70" />
					</IconButton>
				</Tooltip>
			)}
			{isDefaultSiteName(siteName) && <DefaultNameTag />}
		</div>
	);
};

export default SiteNameEditor;
